import axios from "axios";
import { useEffect, useState, useRef } from "react";
import { useBlocker, useNavigate } from "react-router-dom";

import { ChatHeader } from "./components/chat-header";
import { ChatInput } from "./components/chat-input";
import { ChatMessageList } from "./components/chat-message-list";
import { NameProjectDialog } from "./components/name-project-dialog";
import { RfpGenerateButton } from "./components/rfp-generate-button";
import { SectorSelection } from "./components/sector-selection";
import icWarningTriangle from "@/assets/icons/ic-warning-triangle.svg";

import { ConfirmDialog } from "@/components/common";

import { startChat, sendChatMessage, type ChatMessage } from "@/services/chat-service";

export const STORAGE_KEY = "chat_session";

interface StoredChatSession {
  projectName: string;
  messages: ChatMessage[];
  currentStep: number;
  totalSteps: number;
  stepHistory: Array<{ role: "user" | "assistant"; content: string }>;
  isCompleted: boolean;
  sector?: string | null;
  valueChain?: string | null;
}

export const ChatPage = () => {
  const navigate = useNavigate();

  const [projectName, setProjectName] = useState("User님의 프로젝트");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [totalSteps, setTotalSteps] = useState(0);
  const [stepHistory, setStepHistory] = useState<
    Array<{ role: "user" | "assistant"; content: string }>
  >([]);

  const [showNameDialog, setShowNameDialog] = useState(false);
  const [showRfpDialog, setShowRfpDialog] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [isChatCompleted, setIsChatCompleted] = useState(false);

  // Sector / Value Chain 상태
  const [sector, setSector] = useState<string | null>(null);
  const [valueChain, setValueChain] = useState<string | null>(null);
  const [isSectorSelected, setIsSectorSelected] = useState(false);

  /**
   * RFP READY
   */
  const isRfpReady = isChatCompleted;

  /**
   * DIRTY STATE
   * The session is considered dirty whenever the user has either renamed the
   * project from its default name or sent at least one chat message. Either
   * change should trigger the exit warning.
   */
  const isDirty = projectName !== "User님의 프로젝트" || messages.length > 1;

  /**
   * BLOCK NAVIGATION
   */
  const isNavigatingRef = useRef(false);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !isNavigatingRef.current && isDirty && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (blocker.state === "blocked") {
      if (isNavigatingRef.current) {
        blocker.proceed?.();
      } else {
        // Otherwise, it's a real accidental exit (e.g., user clicked back button)
        setShowExitDialog(true);
      }
    }
  }, [blocker]);

  /**
   * SAVE SESSION
   */
  useEffect(() => {
    // Only persist when the user has actually interacted (sent at least one
    // message). messages.length > 1 means there is more than just the initial
    // assistant greeting, so we don't save a session the user never engaged with.
    const hasUserMessage = messages.some((m) => m.role === "user");
    if (hasUserMessage || projectName !== "User님의 프로젝트") {
      const session: StoredChatSession = {
        projectName,
        messages,
        currentStep,
        totalSteps,
        stepHistory,
        isCompleted: isChatCompleted,
        sector,
        valueChain,
      };

      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    }
  }, [
    projectName,
    messages,
    currentStep,
    totalSteps,
    stepHistory,
    isChatCompleted,
    sector,
    valueChain,
  ]);

  /**
   * RESTORE SESSION
   */
  useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);

    if (stored) {
      try {
        const session: StoredChatSession = JSON.parse(stored);

        // If the user never sent any message (only the initial assistant
        // greeting exists), discard the stored session so the sector/value
        // chain selection screen is shown again.
        const hasUserMessage = session.messages.some((m) => m.role === "user");
        if (!hasUserMessage && session.projectName === "User님의 프로젝트") {
          sessionStorage.removeItem(STORAGE_KEY);
          return; // fall through → show sector selection
        }

        setProjectName(session.projectName);
        setMessages(session.messages);
        setCurrentStep(session.currentStep);
        setTotalSteps(session.totalSteps);
        setStepHistory(session.stepHistory ?? []);

        setIsChatCompleted(session.isCompleted ?? false);
        setSector(session.sector ?? null);
        setValueChain(session.valueChain ?? null);
        setIsSectorSelected(!!(session.sector && session.valueChain));

        // Resume a reply that was lost while awaiting the API (e.g. mobile
        // discarded/reloaded the tab mid-request). If the last stored message
        // is from the user, no assistant reply was ever received — re-issue
        // the request. The backend /chat/message is stateless, so resending
        // the same payload is idempotent.
        const last = session.messages[session.messages.length - 1];
        if (last && last.role === "user") {
          requestReply(last.content, session.currentStep, session.stepHistory ?? []);
        }

        return;
      } catch {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }

    // No stored session — show sector selection
  }, []);

  /**
   * PAGE REFRESH / TAB CLOSE
   */
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDirty]);

  /**
   * RESUME ON FOREGROUND
   * On mobile the in-flight request can be aborted when the tab is
   * backgrounded without a full reload. When the page becomes visible again,
   * if the last message is still the user's (no reply arrived) and nothing is
   * in flight, re-issue the request. Latest state is read via refs so the
   * listener is bound once.
   */
  const messagesRef = useRef(messages);
  const currentStepRef = useRef(currentStep);
  const stepHistoryRef = useRef(stepHistory);

  // Mirror latest state into refs at commit time (not during render) so the
  // visibilitychange listener — bound once — always reads committed values.
  useEffect(() => {
    messagesRef.current = messages;
    currentStepRef.current = currentStep;
    stepHistoryRef.current = stepHistory;
  });

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        // Cancel the in-flight request when the tab is backgrounded. Mobile
        // browsers (and Chrome on desktop when the tab is discarded) may
        // silently kill the underlying XHR or freeze the tab so the response
        // never settles. Aborting here guarantees `inFlightRef` is cleared in
        // the request's finally-block before we return to the foreground, so
        // the visible-branch below can deterministically re-issue.
        abortInFlight();
        return;
      }

      if (document.visibilityState !== "visible") return;
      if (inFlightRef.current) return;

      const msgs = messagesRef.current;
      const last = msgs[msgs.length - 1];
      if (last && last.role === "user") {
        requestReply(last.content, currentStepRef.current, stepHistoryRef.current);
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    // Safari iOS fires pageshow (with persisted=true when restored from bfcache)
    // rather than visibilitychange in some background scenarios.
    window.addEventListener("pageshow", handleVisibility);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pageshow", handleVisibility);
    };
  }, []);

  /**
   * HELPERS
   */
  const clearSession = () => {
    sessionStorage.removeItem(STORAGE_KEY);
  };

  /**
   * SECTOR SELECTION COMPLETE
   */
  const handleSectorComplete = async (selectedSector: string, selectedValueChain: string) => {
    setSector(selectedSector);
    setValueChain(selectedValueChain);
    setIsSectorSelected(true);

    try {
      const response = await startChat({
        sector: selectedSector,
        value_chain: selectedValueChain,
      });

      const firstMessage: ChatMessage = {
        id: "initial",
        role: "assistant",
        content: response.data.data.first_question,
        timestamp: new Date().toISOString(),
      };

      setMessages([firstMessage]);
      setTotalSteps(response.data.data.total_steps);
      setCurrentStep(0);
    } catch (error) {
      console.error("Failed to start chat session", error);
    }
  };

  /**
   * SEND MESSAGE
   */
  const inFlightRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  const abortInFlight = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  };

  const requestReply = async (
    content: string,
    step: number,
    history: Array<{ role: "user" | "assistant"; content: string }>,
  ) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setIsLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await sendChatMessage(
        {
          message: content,
          current_step: step,
          step_history: history.length ? history : null,
          sector,
          value_chain: valueChain,
        },
        {
          signal: controller.signal,
          timeout: 120_000,
        },
      );

      const data = response.data.data;
      const stepAdvanced = data.current_step !== step;

      const aiMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: data.reply,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, aiMessage]);
      setCurrentStep(data.current_step);

      // Reset step history when step advances; accumulate on CLARIFY/RETRY
      if (stepAdvanced) {
        setStepHistory([]);
      } else {
        setStepHistory((prev: Array<{ role: "user" | "assistant"; content: string }>) => [
          ...prev,
          { role: "user", content },
          { role: "assistant", content: data.reply },
        ]);
      }

      if (data.is_completed) {
        setIsChatCompleted(true);
      }
    } catch (error) {
      // Distinguish "request was cancelled or timed out" (safe to retry —
      // stateless endpoint) from real HTTP failures (do NOT auto-retry, could
      // hammer the backend). Axios raises CanceledError for both explicit
      // aborts and its own timeout.
      const isCancel = axios.isCancel(error);
      const isTimeout = axios.isAxiosError(error) && error.code === "ECONNABORTED";
      const isRetryable = isCancel || isTimeout;

      if (!isCancel) {
        console.error("Failed to send message:", error);
      }

      // Free the guard BEFORE scheduling the retry so the retry can enter.
      inFlightRef.current = false;
      abortRef.current = null;

      // Retry only for transient cancel/timeout AND only while foregrounded.
      // If cancelled because the tab went hidden, the visibilitychange
      // listener will re-issue on return.
      if (isRetryable && document.visibilityState === "visible") {
        const msgs = messagesRef.current;
        const last = msgs[msgs.length - 1];
        if (last && last.role === "user") {
          // Defer so React commits the loading=false transition first.
          setTimeout(() => {
            if (inFlightRef.current) return;
            requestReply(last.content, currentStepRef.current, stepHistoryRef.current);
          }, 500);
        }
      }
      setIsLoading(false);
      return;
    }

    inFlightRef.current = false;
    abortRef.current = null;
    setIsLoading(false);
  };

  /**
   * SEND MESSAGE
   */
  const handleSend = async (content: string) => {
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);

    await requestReply(content, currentStep, stepHistory);
  };

  /**
   * RENAME PROJECT
   */
  const handleRename = (name: string) => {
    setProjectName(name);
    setShowNameDialog(false);
  };

  /**
   * OPEN RFP DIALOG
   */
  const handleRfpClick = () => {
    if (isChatCompleted) {
      handleRfpGenerate();
    } else {
      setShowRfpDialog(true);
    }
  };

  /**
   * GENERATE RFP - Navigate to checklist
   */
  const handleRfpGenerate = async () => {
    setShowRfpDialog(false);

    try {
      // Mark navigation as intentional to avoid blocker
      isNavigatingRef.current = true;

      // Navigate to checklist page
      navigate("/projects/checklist");
    } catch (error) {
      console.error("Failed to generate RFP:", error);
    }
  };

  /**
   * EXIT CONFIRM
   */
  const handleExitConfirm = () => {
    clearSession();

    setShowExitDialog(false);

    blocker?.proceed?.();
  };

  /**
   * EXIT CANCEL
   */
  const handleExitCancel = () => {
    setShowExitDialog(false);

    blocker?.reset?.();
  };

  // ──────────────────────────────────────────────────────────
  // Sector 미선택 → 선택 화면
  // ──────────────────────────────────────────────────────────
  if (!isSectorSelected) {
    return (
      <div className="flex h-[calc(100vh-89px)] flex-col overflow-hidden">
        <SectorSelection onComplete={handleSectorComplete} />
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────
  // 채팅 화면
  // ──────────────────────────────────────────────────────────
  return (
    <div className="flex h-[calc(100vh-89px)] flex-col overflow-hidden">
      <ChatHeader projectName={projectName} onEditName={() => setShowNameDialog(true)} />

      <div className="flex min-h-0 flex-1 flex-col px-5 lg:mx-auto lg:max-w-[1120px] min-[1120px]:px-0">
        <ChatMessageList messages={messages} isLoading={isLoading} />

        <div className="flex shrink-0 flex-col items-center gap-4 py-10 lg:px-[160px] lg:pb-[100px]">
          <RfpGenerateButton disabled={!isRfpReady} onClick={handleRfpClick} />

          <ChatInput onSend={handleSend} disabled={isLoading || currentStep === totalSteps} />
        </div>
      </div>

      <NameProjectDialog
        open={showNameDialog}
        currentName={projectName}
        onConfirm={handleRename}
        onClose={() => setShowNameDialog(false)}
      />

      <ConfirmDialog
        open={showRfpDialog}
        onOpenChange={(open) => {
          if (!open) setShowRfpDialog(false);
        }}
        iconVariant="warning"
        icon={<img src={icWarningTriangle} alt="" width={32} height={32} />}
        title="요구사항정의서 생성 전 확인"
        description=" 요구사항정의서 작성을 위한 정보가 아직 충분히 수집되지 않았습니다.
          그래도 리포트를 생성하시겠습니까? 누락된 정보는 빈칸으로 표시되며,
          생성 후 리포트 섹션에서 직접 수정하실 수 있습니다. 요구사항정의서가
          생성되면 현재 채팅창의 내용은 삭제됩니다."
        primaryAction={{ label: "생성하기", onClick: handleRfpGenerate }}
        secondaryAction={{
          label: "채팅 계속하기",
          onClick: () => setShowRfpDialog(false),
        }}
      />

      <ConfirmDialog
        open={showExitDialog}
        onOpenChange={(open) => {
          if (!open) handleExitCancel();
        }}
        iconVariant="warning"
        icon={<img src={icWarningTriangle} alt="" width={32} height={32} />}
        title="프로젝트 내용 삭제 확인"
        description="현재 논의 중인 프로젝트와 관련된 내용이 삭제됩니다. 계속 진행하시겠습니까?"
        primaryAction={{ label: "네", onClick: handleExitConfirm }}
        secondaryAction={{ label: "아니요", onClick: handleExitCancel }}
      />
    </div>
  );
};
